import { Inbox, LoaderCircle, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { ProblemBanner } from '../feedback/problem-banner';
import { Skeleton } from '../ui/skeleton';

export interface EmptyStateProps {
  icon?: LucideIcon;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
  size?: 'default' | 'sm';
}

export function EmptyState({
  icon: Icon = Inbox,
  title,
  description,
  action,
  className,
  size = 'default',
}: EmptyStateProps) {
  return (
    <div
      data-slot="empty-state"
      className={cn(
        'flex flex-col items-center justify-center rounded-lg border border-border border-dashed bg-surface-raised text-center',
        size === 'sm' ? 'gap-2 p-6' : 'gap-3 p-12',
        className,
      )}
    >
      <Icon
        className={cn('text-fg-subtle', size === 'sm' ? 'size-6' : 'size-8')}
        aria-hidden="true"
      />
      <div className="space-y-1">
        <p className="font-medium text-fg">{title}</p>
        {description ? <p className="max-w-md text-fg-muted text-sm">{description}</p> : null}
      </div>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}

const skeletonKeys = (count: number): string[] =>
  Array.from({ length: count }, (_, n) => `row-${n}`);

export function LoadingState({
  label = 'Loading…',
  rows = 3,
  className,
}: {
  label?: string;
  rows?: number;
  className?: string;
}) {
  return (
    <div role="status" aria-live="polite" className={cn('space-y-3', className)}>
      <span className="sr-only">{label}</span>
      {skeletonKeys(rows).map((key) => (
        <Skeleton key={key} className="h-10 w-full" />
      ))}
    </div>
  );
}

export function Spinner({ className, label = 'Loading' }: { className?: string; label?: string }) {
  return (
    <LoaderCircle
      role="status"
      aria-label={label}
      className={cn('size-4 animate-spin text-fg-muted', className)}
    />
  );
}

export interface QueryStateProps<T> {
  data: T | undefined;
  isPending: boolean;
  error: unknown;
  onRetry?: () => void;
  /** Rendered when the data is an empty array (or `isEmpty` says so). */
  empty?: ReactNode;
  isEmpty?: (data: T) => boolean;
  loading?: ReactNode;
  children: (data: T) => ReactNode;
}

/**
 * Loading / error / empty / ready switch for one query:
 *
 *   <QueryState {...projects} empty={<EmptyState title="No projects" />}>{(page) => …}</QueryState>
 */
export function QueryState<T>({
  data,
  isPending,
  error,
  onRetry,
  empty,
  isEmpty = (d) => Array.isArray(d) && d.length === 0,
  loading,
  children,
}: QueryStateProps<T>) {
  if (isPending) return loading ?? <LoadingState />;
  if (error) return <ProblemBanner error={error} onRetry={onRetry} />;
  if (data === undefined) return null;
  if (empty !== undefined && isEmpty(data)) return empty;
  return children(data);
}
